import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Role arrays used for nav filtering
const R_OPS  = ['tenant_admin', 'manager', 'attendant', 'employee'];
const R_HALF = ['tenant_admin', 'manager', 'attendant'];
const R_MGR  = ['tenant_admin', 'manager'];
const R_FIN  = ['tenant_admin', 'manager', 'financial'];
const R_ADM  = ['tenant_admin'];

const NAV_TENANT = [
  { label: 'Dashboard',    icon: '📊', path: '/dashboard' },
  { label: 'Agenda',       icon: '📅', path: '/appointments',  roles: R_OPS },
  { label: 'O.S.',         icon: '📋', path: '/orders',        roles: R_OPS },
  { label: 'Clientes',     icon: '👥', path: '/customers',     roles: R_HALF },
  { label: 'Veículos',     icon: '🚗', path: '/vehicles',      roles: R_HALF },
  { label: 'Serviços',     icon: '🔧', path: '/services',      roles: R_MGR },
  { label: 'Pacotes',      icon: '📦', path: '/packages',      roles: R_MGR, feature: 'has_packages' },
  { label: 'Financeiro',   icon: '💰', path: '/financial',     roles: R_FIN, feature: 'has_financial' },
  { label: 'Estoque',      icon: '🏭', path: '/stock',         roles: R_MGR, feature: 'has_stock' },
  { label: 'Funcionários', icon: '👷', path: '/employees',     roles: R_MGR, feature: 'has_commissions' },
  { label: 'Relatórios',   icon: '📈', path: '/reports',       roles: R_FIN, feature: 'has_reports' },
  { label: 'Landing Page', icon: '🌐', path: '/landing',       roles: R_MGR, feature: 'has_landing_page' },
  { label: 'Usuários',     icon: '👤', path: '/users',         roles: R_ADM },
  { label: 'Assinatura',   icon: '💳', path: '/billing',       roles: R_ADM },
  { label: 'Configurações',icon: '⚙️', path: '/settings',      roles: R_ADM },
];

const NAV_ADMIN = [
  { label: 'Métricas',        icon: '📊', path: '/admin' },
  { label: 'Clientes',        icon: '🏢', path: '/admin/tenants' },
  { label: 'Planos',          icon: '💳', path: '/admin/plans' },
  { label: 'Cupons',          icon: '🎟️', path: '/admin/coupons' },
  { label: 'Configurações',   icon: '⚙️', path: '/admin/settings' },
  { label: 'Ferramentas',     icon: '🔧', path: '/admin/tools' },
];

export default function Layout({ children }) {
  const [sideOpen, setSideOpen] = useState(false);
  const { user, logout, isSuperAdmin, hasFeature } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = isSuperAdmin ? NAV_ADMIN : NAV_TENANT.filter(
    n => (!n.feature || hasFeature(n.feature)) && (!n.roles || n.roles.includes(user?.role))
  );

  const tenantName = user?.tenant_name || 'AutoFlow';
  const logoUrl    = user?.tenant_logo ? `/uploads/${user.tenant_logo}` : null;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', sans-serif", background: '#F8FAFC' }}>
      {/* Overlay mobile */}
      {sideOpen && (
        <div onClick={() => setSideOpen(false)}
          style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:40 }} />
      )}

      {/* SIDEBAR */}
      <aside style={{
        width: 240, background: '#0F172A', color: '#fff', flexShrink: 0,
        position: 'fixed', top: 0, left: sideOpen ? 0 : -240, height: '100vh',
        zIndex: 50, transition: 'left .25s', display: 'flex', flexDirection: 'column',
        '@media(min-width:768px)': { left: 0 }
      }} className="sidebar">
        {/* Logo */}
        <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          {logoUrl
            ? <img src={logoUrl} alt="Logo" style={{ height: 40, objectFit: 'contain' }} />
            : <div style={{ fontWeight: 900, fontSize: '1.2rem', color: '#60A5FA' }}>🚗 {tenantName}</div>
          }
          {isSuperAdmin && <div style={{ fontSize: '.7rem', color: '#94a3b8', marginTop: 4 }}>Super Admin</div>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
          {navItems.map(n => {
            const active = location.pathname.startsWith(n.path) && (n.path !== '/admin' || location.pathname === '/admin');
            return (
              <Link key={n.path} to={n.path}
                onClick={() => setSideOpen(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 20px', textDecoration: 'none',
                  color: active ? '#fff' : 'rgba(255,255,255,.65)',
                  background: active ? 'rgba(37,99,235,.4)' : 'transparent',
                  borderLeft: active ? '3px solid #2563EB' : '3px solid transparent',
                  fontSize: '.9rem', transition: 'all .15s',
                }}>
                <span style={{ fontSize: '1.1rem' }}>{n.icon}</span>
                {n.label}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <div style={{ fontSize: '.85rem', fontWeight: 600, color: '#fff' }}>{user?.name}</div>
          <div style={{ fontSize: '.75rem', color: '#94a3b8', marginBottom: 8 }}>{user?.email}</div>
          <button onClick={logout}
            style={{ background: 'rgba(239,68,68,.2)', border: 'none', color: '#fca5a5',
              borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: '.8rem', width: '100%' }}>
            Sair
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div style={{ flex: 1, marginLeft: 0, display: 'flex', flexDirection: 'column' }} className="main-content">
        {/* Header */}
        <header style={{
          background: '#fff', borderBottom: '1px solid #E5E7EB',
          padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16,
          position: 'sticky', top: 0, zIndex: 30,
        }}>
          <button onClick={() => setSideOpen(s => !s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: '#374151' }}>
            ☰
          </button>
          <div style={{ flex: 1 }}>
            {user?.tenant_status === 'trial' && (() => {
              const days = user?.trial_ends_at
                ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000))
                : null;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: days === 0 ? '#FEE2E2' : '#FEF3C7', color: days === 0 ? '#DC2626' : '#92400E', borderRadius: 6, padding: '3px 10px', fontSize: '.75rem', fontWeight: 600 }}>
                    ⏳ {days === null ? 'Período de teste' : days > 0 ? `Teste: ${days} dia${days !== 1 ? 's' : ''}` : 'Teste expirado'}
                  </span>
                  <button onClick={() => navigate('/billing')}
                    style={{ padding: '3px 10px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, fontSize: '.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    Assinar
                  </button>
                </div>
              );
            })()}
          </div>
          <div style={{ fontSize: '.85rem', color: '#6B7280' }}>
            {new Date().toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long' })}
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: '24px', maxWidth: 1280, width: '100%', margin: '0 auto' }}>
          {children}
        </main>
      </div>

      {/* Blocking overlay for overdue / cancelled accounts */}
      {['overdue', 'cancelled'].includes(user?.tenant_status) && !isSuperAdmin && location.pathname !== '/billing' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 40, maxWidth: 420, textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔒</div>
            <h2 style={{ fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>
              {user.tenant_status === 'cancelled' ? 'Assinatura cancelada' : 'Pagamento em aberto'}
            </h2>
            <p style={{ color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
              {user.tenant_status === 'cancelled'
                ? 'Sua assinatura foi cancelada. Renove para continuar usando o AutoFlow.'
                : 'Há um pagamento em aberto. Regularize para continuar usando o sistema.'}
            </p>
            <button onClick={() => navigate('/billing')}
              style={{ padding: '12px 32px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem', cursor: 'pointer', width: '100%' }}>
              {user.tenant_status === 'cancelled' ? 'Renovar assinatura' : 'Regularizar pagamento'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media(min-width:768px) {
          .sidebar { left: 0 !important; }
          .main-content { margin-left: 240px !important; }
        }
      `}</style>
    </div>
  );
}
